import React from 'react';
import { Bar } from 'react-chartjs-2';
import Skeleton from 'react-loading-skeleton';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { MonthlySalesItem, MonthlySalesChartData } from '../types/monthlySalesChart.types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface MonthlySalesChartProps {
  data: MonthlySalesItem[] | null;
  loading: boolean;
  error: string | null;
}

const chartOptions = {
  responsive: true,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
      text: 'Leads and Conversions',
    },
    labels: {
      color: '#1C1C1C66',
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: {
        display: false,
      },
      ticks: {
        color: '#1C1C1C66',
      },
      border: {
        display: false,
      },
    },
    y: {
      stacked: true,
      grid: {
        display: false,
      },
      border: {
        display: false,
      },
      ticks: {
        color: '#1C1C1C66',
      },
    },
  },
};

const monthLabels = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Transforms MonthlySalesItem array into Chart.js compatible data
 */
const transformChartData = (items: MonthlySalesItem[]): MonthlySalesChartData => {
  const totalSalesData = items.map((item) => item.totalSales || 0);
  const totalBillableLeadsData = items.map(
    (item) => item.totalBillableLeads || 0
  );

  return {
    labels: monthLabels,
    datasets: [
      {
        label: 'Total Sales',
        data: totalSalesData,
        backgroundColor: '#95A4FC',
        barThickness: 28,
        borderRadius: 5,
      },
      {
        label: 'Total Billable Leads',
        data: totalBillableLeadsData,
        backgroundColor: 'rgba(149, 164, 252, .5)',
        barThickness: 28,
        borderRadius: 5,
      },
    ],
  };
};

/**
 * Presentational component for monthly sales and leads bar chart
 */
const MonthlySalesChart: React.FC<MonthlySalesChartProps> = ({
  data,
  loading,
  error,
}) => {
  if (loading) {
    return (
      <div className="chart-container">
        <Skeleton height={200} />
      </div>
    );
  }

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  if (!data || data.length === 0) {
    return <p className="no-data-message">No data available</p>;
  }

  const chartData = transformChartData(data);

  return (
    <div className="l-vs-c-r-container">
      <div>
        <span>Leads vs Conversion Rate</span>
      </div>
      <Bar data={chartData} width={800} options={chartOptions} />
      <div className="monthly-bar-legend">
        <div>
          <div className="lead-indicator"></div>
          <span>Lead</span>
        </div>
        <div>
          <div className="conversion-indicator"></div>
          <span>Conversion Rate</span>
        </div>
      </div>
    </div>
  );
};

export default MonthlySalesChart;
